import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/app-errors";
import { captureError } from "@/lib/observability";
import type { ClaudiaQuickReply } from "../components/claudia-quick-reply-context";
import { buildNextStepsReplyMessage } from "../lib/next-steps-replies";

/** How long the pressed row says `Sent` before the section goes back to plain markdown. */
export const NEXT_STEPS_SENT_BEAT_MS = 900;

export interface NextStepsReplyState {
  sendingLetter: string | null;
  sentLetter: string | null;
  select: (letter: string) => void;
}

/**
 * The pressed and sent letters of one answer's option block.
 *
 * They are held here, on the row, rather than derived from the thread: the optimistic user bubble
 * is appended the instant the send starts, so by the time the mutation resolves this answer is no
 * longer the last thing in the transcript and the options would collapse before the press had
 * been seen at all. The letters keep the pressed and the sent treatments on screen until the beat
 * ends, by which time the answer is genuinely history and the section renders as the plain
 * markdown the model wrote.
 */
export function useNextStepsReply(quickReply: ClaudiaQuickReply | null): NextStepsReplyState {
  const [sendingLetter, setSendingLetter] = useState<string | null>(null);
  const [sentLetter, setSentLetter] = useState<string | null>(null);
  // State cannot answer "is a send already in flight" for a second press in the same frame.
  const inFlightLetterRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const beatTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (beatTimerRef.current !== null) {
        clearTimeout(beatTimerRef.current);
        beatTimerRef.current = null;
      }
    };
  }, []);

  const select = useCallback(
    async (letter: string) => {
      if (inFlightLetterRef.current !== null || !quickReply?.canSend) {
        return;
      }
      inFlightLetterRef.current = letter;
      setSendingLetter(letter);
      try {
        const accepted = await quickReply.sendReply(buildNextStepsReplyMessage(letter));
        if (!mountedRef.current || !accepted) {
          // The composer reports its own failures; the row simply becomes an offer again.
          return;
        }
        setSentLetter(letter);
        beatTimerRef.current = setTimeout(() => {
          beatTimerRef.current = null;
          if (mountedRef.current) {
            setSentLetter(null);
          }
        }, NEXT_STEPS_SENT_BEAT_MS);
      } catch (cause: unknown) {
        captureError(cause, { feature: "assistant", operation: "next-steps-reply" });
        if (mountedRef.current) {
          toast.error(getErrorMessage(cause, "Failed to send reply"));
        }
      } finally {
        inFlightLetterRef.current = null;
        if (mountedRef.current) {
          setSendingLetter(null);
        }
      }
    },
    [quickReply]
  );

  return { sendingLetter, sentLetter, select };
}
